import { useState, useRef, useCallback, useEffect } from "react";
import { AlertTriangle, X, Send, Loader2, Camera, RotateCcw } from "lucide-react";
import { NovedadesService } from "../service/NovedadesService";
import { toast } from "react-toastify";
import Portal from "./Portal.jsx";
import "../../../css/OnRoad.css";

export default function NovedadReportingModal({ isOpen, onClose, manifiesto, conductor, placa }) {
    // Estados principales
    const [tipoNovedad, setTipoNovedad] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [isReporting, setIsReporting] = useState(false);

    // Estados para cámara y foto
    const [fileupload, setFileupload] = useState(null);
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [facingMode, setFacingMode] = useState('environment');

    // Referencias para cámara
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    // Función para formatear fecha y hora (igual que EventReportingSheet)
    const formatDateTime = (date) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
               `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    // Iniciar cámara
    const startCamera = useCallback(async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode }
            });
            streamRef.current = mediaStream;
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play();
            }
        } catch (err) {
            console.error('Error al acceder a la cámara:', err);
            toast.error('No se pudo acceder a la cámara');
        }
    }, [facingMode]);

    // Detener cámara
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    }, []);

    // Effect para manejar la cámara cuando se abre/cierra el modal de cámara
    useEffect(() => {
        if (showCameraModal) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [showCameraModal, startCamera, stopCamera]);

    // Effect para limpiar la cámara cuando se cierra el modal principal
    useEffect(() => {
        if (!isOpen) {
            stopCamera();
        }
    }, [isOpen, stopCamera]);

    // Capturar foto con timestamp
    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;

        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Superponer fecha y hora
        const fh = formatDateTime(new Date());
        ctx.font = '20px Arial';
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.strokeText(fh, 10, canvas.height - 20);
        ctx.fillText(fh, 10, canvas.height - 20);

        canvas.toBlob(blob => {
            if (!blob) return;
            const file = new File([blob], `novedad-evidencia-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFileupload(file);
            toast.success('Imagen capturada correctamente');
            setShowCameraModal(false);
        }, 'image/jpeg', 0.9);
    };

    // Eliminar foto
    const eliminarFoto = () => {
        setFileupload(null);
        toast.info('Foto eliminada. Puede tomar una nueva foto.');
    };

    // Alternar cámara frontal/trasera
    const toggleCamera = () => {
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    };

    // Handler para cerrar el modal
    const handleClose = () => {
        if (isReporting) return;
        
        // Limpiar estados
        setTipoNovedad('');
        setObservaciones('');
        setFileupload(null);
        stopCamera();
        onClose?.();
    };

    // Handler para enviar la novedad
    const handleSubmit = async () => {
        // Validación: tipo de novedad es obligatorio
        if (!tipoNovedad.trim()) {
            toast.warning('Debe especificar el tipo de novedad');
            return;
        }

        if (isReporting) return;

        // Iniciar loading
        setIsReporting(true);

        const toastId = toast.info("Enviando novedad...", {
            autoClose: false,
            closeButton: false,
            hideProgressBar: false
        });

        try {
            // Preparar el tipo de novedad
            const novedadTipo = tipoNovedad.trim();
            
            // Preparar observaciones completas
            let observacionesCompletas = observaciones.trim();
            if (!observacionesCompletas) {
                observacionesCompletas = `Tipo: ${novedadTipo}. Reportado desde OnRoad.`;
            } else {
                observacionesCompletas = `Tipo: ${novedadTipo}. Observaciones: ${observacionesCompletas}`;
            }
            
            // Llamar al servicio de NovedadesService con evidencia
            const resultado = await NovedadesService.reportarNovedades({
                novedades: novedadTipo,
                observaciones: observacionesCompletas,
                evidencia: fileupload // Enviar la foto si existe
            });

            // Cerrar toast de loading
            toast.dismiss(toastId);

            if (resultado.success) {
                // Toast de éxito
                const mensajeExito = fileupload 
                    ? `Novedad "${novedadTipo}" enviada con evidencia fotográfica`
                    : `Novedad "${novedadTipo}" enviada exitosamente`;
                
                toast.success(resultado.message || mensajeExito, {
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });

                // Cerrar modal
                handleClose();
            } else {
                // Error del servicio
                throw new Error(resultado.message || 'Error al enviar la novedad');
            }

        } catch (error) {
            console.error('Error enviando novedad:', error);
            toast.dismiss(toastId);
            
            // Toast de error detallado
            const mensajeError = error.message || 'Error al enviar la novedad. Intente nuevamente.';
            toast.error(mensajeError, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            // Detener loading
            setIsReporting(false);
        }
    };

    // Handler para tecla Enter en el campo de tipo
    const handleTipoKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (tipoNovedad.trim() && !isReporting) {
                handleSubmit();
            }
        }
    };

    // Handler para tecla Escape
    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && !isReporting) {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <Portal>
            <div 
                className="sheet-backdrop novedad-modal-backdrop"
                onKeyDown={handleKeyDown}
            >
                <div className="sheet-container novedad-modal">
                    {/* Header */}
                    <div className="sheet-header">
                        <div className="sheet-title">
                            <AlertTriangle size={20} />
                            Reportar Novedad
                        </div>
                        <button
                            className="sheet-close-btn"
                            onClick={handleClose}
                            disabled={isReporting}
                            aria-label="Cerrar"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="sheet-content">
                        {/* Información básica */}
                        <div className="event-info-section-compact">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Manifiesto</span>
                                    <span className="info-value">{manifiesto}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Conductor</span>
                                    <span className="info-value">{conductor}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Placa</span>
                                    <span className="info-value">{placa}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Hora</span>
                                    <span className="info-value">
                                        {new Date().toLocaleTimeString('es-CO', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Campo de Tipo de Novedad - OBLIGATORIO */}
                        <div className="novedad-tipo-section">
                            <label className="section-label-compact">
                                Tipo de Novedad 
                                <span className="required-label">(Obligatorio)</span>
                            </label>
                            <input
                                type="text"
                                className="novedad-tipo-input"
                                placeholder="Ej: Accidente de tránsito, Falla mecánica, Demora por tráfico..."
                                value={tipoNovedad}
                                onChange={(e) => setTipoNovedad(e.target.value)}
                                onKeyDown={handleTipoKeyDown}
                                maxLength={100}
                                disabled={isReporting}
                                autoFocus
                                aria-required="true"
                                aria-label="Tipo de novedad"
                            />
                            <div className="char-count-compact">
                                {tipoNovedad.length}/100
                            </div>
                        </div>

                        {/* Campo de Observaciones - OPCIONAL */}
                        <div className="observations-section-compact">
                            <label className="section-label-compact">
                                Observaciones Adicionales 
                                <span className="optional-label">(Opcional)</span>
                            </label>
                            <textarea
                                className="observations-textarea-compact"
                                placeholder="Proporcione detalles adicionales sobre la novedad..."
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                maxLength={500}
                                disabled={isReporting}
                                rows={4}
                                aria-label="Observaciones adicionales"
                            />
                            <div className="char-count-compact">
                                {observaciones.length}/500
                            </div>
                        </div>

                        {/* Evidencia fotográfica - NUEVO */}
                        <div className="photo-section-compact">
                            <label className="section-label-compact">
                                Evidencia Fotográfica 
                                <span className="optional-label">(Opcional)</span>
                            </label>
                            
                            {!fileupload ? (
                                <button 
                                    className="photo-button-full"
                                    type="button"
                                    onClick={() => setShowCameraModal(true)}
                                    disabled={isReporting}
                                >
                                    <Camera size={16} />
                                    Tomar Foto
                                </button>
                            ) : (
                                <div className="photo-preview-enhanced">
                                    {/* Vista previa de la imagen */}
                                    <div className="photo-preview-container">
                                        <img 
                                            src={URL.createObjectURL(fileupload)} 
                                            alt="Evidencia capturada"
                                            className="photo-preview-image"
                                        />
                                        <button
                                            type="button"
                                            className="photo-quick-remove"
                                            onClick={eliminarFoto}
                                            disabled={isReporting}
                                            title="Eliminar foto"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    
                                    {/* Botones de acción */}
                                    <div className="photo-actions-container">
                                        <button 
                                            className="photo-action-btn secondary"
                                            type="button"
                                            onClick={() => setShowCameraModal(true)}
                                            disabled={isReporting}
                                        >
                                            <Camera size={14} />
                                            Tomar Nueva Foto
                                        </button>
                                        
                                        <button 
                                            className="photo-action-btn danger"
                                            type="button"
                                            onClick={eliminarFoto}
                                            disabled={isReporting}
                                        >
                                            <X size={14} />
                                            Eliminar Foto
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal de Cámara - NUEVO */}
                        {showCameraModal && (
                            <div className="sheet-backdrop" style={{ zIndex: 9999 }}>
                                <div className="camera-modal-container">
                                    <div className="camera-modal-header">
                                        <div className="camera-modal-title">
                                            <Camera size={20} />
                                            Capturar Evidencia
                                        </div>
                                        <button 
                                            className="camera-close-btn"
                                            onClick={() => setShowCameraModal(false)}
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                    
                                    <div className="camera-modal-content">
                                        <div className="camera-viewport">
                                            <video
                                                ref={videoRef}
                                                autoPlay
                                                playsInline
                                                className="camera-video"
                                            />
                                        </div>
                                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                                    </div>
                                    
                                    <div className="camera-modal-footer">
                                        <button
                                            className="camera-control-btn secondary"
                                            onClick={toggleCamera}
                                            title="Cambiar cámara"
                                        >
                                            <RotateCcw size={16} />
                                            <span>Rotar</span>
                                        </button>
                                        
                                        <button
                                            className="camera-control-btn primary capture"
                                            onClick={capturePhoto}
                                        >
                                            <Camera size={20} />
                                            <span>Capturar</span>
                                        </button>
                                        
                                        <button
                                            className="camera-control-btn secondary"
                                            onClick={() => setShowCameraModal(false)}
                                            title="Cancelar"
                                        >
                                            <X size={16} />
                                            <span>Cancelar</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="sheet-footer">
                        <button
                            className="btn-report-event"
                            onClick={handleSubmit}
                            disabled={!tipoNovedad.trim() || isReporting}
                        >
                            {isReporting ? (
                                <>
                                    <Loader2 size={20} className="spinner-small" />
                                    Enviando Novedad...
                                </>
                            ) : (
                                <>
                                    <Send size={20} />
                                    Enviar Novedad
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Portal>
    );
}